import Image from '@tiptap/extension-image';

import { RICH_TEXT_IMAGE_REF_ATTR } from './sanitizeRichText';
import { fetchFormImageObjectUrl } from './formImageResolver';

/**
 * The rich-text image node (TAV-3155). Extends the stock Tiptap `Image` extension rather than
 * building a node from scratch, for its `group`/`draggable` schema shape and `deleteNode`/
 * `insertContent` command wiring, but its attribute set and parse rule are entirely replaced.
 */
export const RichTextImage = Image.extend({
  // The stock extension sets `draggable: true`. Turning it off removes the drag affordance from an
  // unselected image, which is the common gesture. It is deliberately NOT the whole guard: selecting
  // an image makes prosemirror-view's NodeViewDesc.selectNode set draggable back on the DOM, and its
  // dragstart handler then drags the selection without consulting this flag. The check that actually
  // refuses the drop lives in RichTextEditor's handleDrop, which reads view.dragging.
  draggable: false,
  addAttributes() {
    // Deliberately NOT spreading this.parent?.(): the stock extension's `src` and `title`
    // attributes must not exist on this node, because whatever is in the attributes is what
    // getHTML() serialises and therefore what gets persisted forever. Display is a node-view
    // concern, handled entirely below. It never touches the persisted attribute set.
    return {
      imageId: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute(RICH_TEXT_IMAGE_REF_ATTR),
        renderHTML: (attrs: Record<string, any>) =>
          attrs.imageId ? { [RICH_TEXT_IMAGE_REF_ATTR]: attrs.imageId } : {},
      },
      width: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('width'),
        renderHTML: (attrs: Record<string, any>) => (attrs.width ? { width: attrs.width } : {}),
      },
      alt: {
        default: '',
        // Reads from `attrs` like the two above it, so parse and serialise are the same direction.
        // There is no alt-text input yet, so this always emits alt="" today; the point is that when
        // one is added, the value survives the next getHTML() instead of being silently dropped.
        parseHTML: (el: HTMLElement) => el.getAttribute('alt') ?? '',
        renderHTML: (attrs: Record<string, any>) => ({ alt: attrs.alt ?? '' }),
      },
    };
  },
  // The stock rule is `img[src]`, which would never match our markup AND would happily adopt a
  // pasted hotlinked image. Requiring the reference attribute makes a foreign <img> fail to parse
  // into a node at all. AC8 enforced by the schema rather than by a filter.
  parseHTML() {
    return [{ tag: `img[${RICH_TEXT_IMAGE_REF_ATTR}]` }];
  },
  // Plain DOM node view, not React: ProseMirror's editor DOM is built here, while `getHTML()`
  // (persisted output) comes from `renderHTML` above. The two are allowed to differ, and that is
  // the whole reason the persisted attribute set stays clean with no serialise-then-strip step.
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('img');
      dom.className = 'rich-text-image-placeholder';
      let objectUrl: string | null = null;
      let destroyed = false;

      const applyAttrs = (attrs: Record<string, any>) => {
        dom.alt = attrs.alt ?? '';
        if (attrs.width) dom.setAttribute('width', String(attrs.width));
        else dom.removeAttribute('width');
      };

      applyAttrs(node.attrs);

      if (node.attrs.imageId) {
        fetchFormImageObjectUrl(node.attrs.imageId)
          .then((url) => {
            // Mirrors RichTextView's in-flight guard: destroy() already ran and will not run again
            // for this URL, so revoke it here rather than leaking it into a closure nothing calls.
            if (destroyed) {
              URL.revokeObjectURL(url);
              return;
            }
            objectUrl = url;
            dom.src = url;
            dom.classList.remove('rich-text-image-placeholder');
          })
          .catch(() => {
            // Nothing else displayable, so the placeholder styling stays.
          });
      }

      return {
        dom,
        update: (updatedNode) => {
          // `dom.src` is set once, in the fetch below, closing over the node this view was created
          // with, and ProseMirror only compares node TYPE before reusing a view (prosemirror-view
          // CustomNodeViewDesc.update). So accepting a node with a different reference would leave
          // the previous image's blob on screen. Declining lets ProseMirror rebuild the view, which
          // re-runs the fetch and calls destroy() to revoke the old URL.
          if (updatedNode.attrs.imageId !== node.attrs.imageId) return false;
          applyAttrs(updatedNode.attrs);
          return true;
        },
        destroy: () => {
          destroyed = true;
          if (objectUrl) URL.revokeObjectURL(objectUrl);
        },
      };
    };
  },
});
